import { Form, Formik } from "formik";
import { Role } from "../../types/Role";
import { PageHeader } from "../PageHeader/PageHeader";
import RestrictedTo from "../RestrictedTo/RestrictedTo";
import styles from "./AdminDataPermission.module.scss";
import { Grid, GridItem } from "../Grid/Grid";
import { MultiSelect } from "../MultiSelect/MultiSelect";
import { Country } from "../../types/Country";
import { TextInput } from "../TextInput/TextInput";
import { Button } from "../Button/Button";
import { Table } from "../Table/Table";
import { Column, SortColumn } from "react-data-grid";
import { useCallback, useEffect, useState } from "react";
import {
  getAndSearchAdminUsers,
  SearchUserCondition,
  updateDataPermissionForAdminUser,
  UserWithDataPermission,
} from "../../api/user";
import { Spinner } from "../Spinner/Spinner";
import { i18n } from "@lingui/core";
import { NoRecordFoundModal } from "../UserManagement/NoRecordFoundModal/NoRecordFoundModal";
import Pagination from "../Paginate/Pagination";
import { isEmpty } from "lodash";
import { DataPermissionByCountry } from "../../types/DataPermissionByCountry";
import { AnnouncementModal } from "../Admin/AnnouncementModal/AnnouncementModal";
import { ProcessFailedModal } from "../Admin/ProcessFailedModal/ProcessFailedModal";
import { convertAdminUserRole } from "../../utils/utils";

interface Row {
  id: string;
  email: string | undefined;
  type: Role | string;
  nickname: string | undefined;
  firstname: string | undefined;
  lastname: string | undefined;
  country: Country;
  isActiveForDEData: boolean;
  isActiveForFRData: boolean;
}

export interface GetAdminUsersWithPagination {
  totalCount: number;
  totalPage: number;
  currentPage: number;
  next?: {
    page: number;
    limit: number;
  };
  previous?: {
    page: number;
    limit: number;
  };
  last?: {
    page: number;
    limit: number;
  };
  paginateData: UserWithDataPermission[];
  currentCountPerPage: number;
  range: number;
}

interface SortOptions {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
}

export const AdminDataPermission = () => {
  const [newRows, setNewRows] = useState<Row[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);
  const [searchCondition, setSearchCondition] = useState<SearchUserCondition>(
    {}
  );
  const [totalUsersCount, setTotalUsersCount] = useState<number>(0);
  const [forcePage, setForcePage] = useState<number>();

  const [isShowNoRecordFoundModal, setIsShowNoRecordFoundModal] =
    useState<boolean>(false);
  const [isShowSucessfullModal, setIsShowSucessfullModal] =
    useState<boolean>(false);
  const [isShowErrorModal, setIsShowErrorModal] = useState<boolean>(false);

  const [isShowSpinner, setIsShowSpinner] = useState<boolean>(false);
  const [isDisableSaveButton, setIsDisableSaveButton] = useState<boolean>(true);
  const [sortOptions, setSortOptions] = useState<SortOptions>({
    page: 1,
    limit: 10,
    sortBy: "country",
    sortOrder: "asc",
  });

  useEffect(() => {
    setIsDisableSaveButton(
      JSON.stringify(newRows) === JSON.stringify(rows) ||
        newRows.some((row) => !row.isActiveForDEData && !row.isActiveForFRData)
    );
  }, [newRows, rows, isDisableSaveButton]);

  const setRowsFromUsersData = useCallback(
    (users: UserWithDataPermission[]) => {
      let rows: Row[] = [];
      for (let user of users) {
        rows.push({
          id: user.id,
          email: user.email,
          type: convertAdminUserRole(user.type),
          nickname: user.nickname,
          firstname: user.firstname,
          lastname: user.lastname,
          country: user.country,
          isActiveForDEData: user.dataPermissions.some(
            (permission) => permission === DataPermissionByCountry.DE_DATA
          ),
          isActiveForFRData: user.dataPermissions.some(
            (permission) => permission === DataPermissionByCountry.FR_DATA
          ),
        });
      }
      setNewRows(rows);
      setRows(rows);
    },
    []
  );

  const fetchUsers = useCallback(
    async (sortOptions: SortOptions, searchCondition: SearchUserCondition) => {
      const users: GetAdminUsersWithPagination | undefined =
        await getAndSearchAdminUsers(sortOptions, searchCondition);

      if (users?.paginateData && users.paginateData.length > 0) {
        setRowsFromUsersData(users.paginateData);
        setTotalUsersCount(users.totalCount);
      }

      setIsShowSpinner(false);
    },
    [setRowsFromUsersData]
  );

  const onSortColumns = useCallback(
    (sortColumns: SortColumn[], searchCondition: SearchUserCondition) => {
      let sortOptionsUpdate = { ...sortOptions };

      if (!isEmpty(sortColumns)) {
        sortOptionsUpdate.sortBy = sortColumns[0].columnKey;
        sortOptionsUpdate.sortOrder = sortColumns[0].direction.toLowerCase() as
          | "asc"
          | "desc";
      }
      setSortOptions(sortOptionsUpdate);

      fetchUsers(sortOptionsUpdate, searchCondition);
      setSortColumns(sortColumns);
    },
    [fetchUsers, sortOptions]
  );

  const columns: Column<Row>[] = [
    {
      key: "id",
      name: "UserID",
      minWidth: 250,
    },
    {
      key: "email",
      name: "Email",
      minWidth: 150,
    },
    {
      key: "type",
      name: "Role",
    },
    {
      key: "nickname",
      name: "Nickname",
    },
    {
      key: "firstname",
      name: "Firstname",
    },
    {
      key: "lastname",
      name: "Lastname",
    },
    {
      key: "country",
      name: "Country",
    },
    {
      key: "dedata",
      name: "DE Data",
      sortable: false,
      renderCell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={row.isActiveForDEData}
          style={{
            width: "16px",
            height: "16px",
          }}
          onChange={(e) => {
            setNewRows((prevRows: Row[]) =>
              prevRows.map((prevRow: Row) =>
                prevRow.id === row.id
                  ? { ...prevRow, isActiveForDEData: e.target.checked }
                  : prevRow
              )
            );
          }}
        />
      ),
      headerCellClass: "center-header",
      cellClass: "center-value-cell",
    },
    {
      key: "frdata",
      name: "FR Data",
      sortable: false,
      renderCell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={row.isActiveForFRData}
          style={{
            width: "16px",
            height: "16px",
          }}
          onChange={(e) =>
            setNewRows((prevRows: Row[]) =>
              prevRows.map((prevRow: Row) =>
                prevRow.id === row.id
                  ? { ...prevRow, isActiveForFRData: e.target.checked }
                  : prevRow
              )
            )
          }
        />
      ),
      headerCellClass: "center-header",
      cellClass: "center-value-cell",
    },
  ];

  return (
    <RestrictedTo roles={[Role.SUPER_ADMIN]} redirects={true}>
      <div className={styles.container}>
        <PageHeader title="Admin Data Permission" />

        <div className={styles.adminDataPermissionContainer}>
          <Formik
            enableReinitialize={true}
            initialValues={{
              country: [],
              userId: "",
              email: "",
              firstName: "",
              lastName: "",
              nickname: "",
              role: [],
            }}
            onSubmit={async (values: any) => {
              setIsShowSpinner(true);
              const searchUserParams: SearchUserCondition = {
                userId: values.userId && values.userId.trim(),
                country: values.country && values.country,
                email: values.email && values.email.trim(),
                firstName: values.firstName && values.firstName.trim(),
                lastName: values.lastName && values.lastName.trim(),
                nickname: values.nickname && values.nickname.trim(),
                role: values.role && values.role,
              };

              const sortOptionsUpdate = { ...sortOptions };
              sortOptionsUpdate.page = 1;

              const searchUsers = await getAndSearchAdminUsers(
                sortOptionsUpdate,
                searchUserParams
              );

              if (
                searchUsers?.paginateData &&
                searchUsers.paginateData.length === 0
              ) {
                setIsShowNoRecordFoundModal(true);
              }

              setRowsFromUsersData(searchUsers.paginateData);
              setTotalUsersCount(searchUsers.totalCount);

              setSearchCondition(searchUserParams);

              // This condition to force component Pagination re-render.
              setForcePage((prev) => (prev === 0 ? undefined : 0));

              setIsShowSpinner(false);
              setIsDisableSaveButton(true);
            }}
          >
            {({ handleChange, values, resetForm }) => (
              <Form>
                <div className={styles.filterContainer}>
                  <div className={styles.filterContainerRow}>
                    <Grid>
                      <GridItem width="1/3">
                        <MultiSelect
                          id="country"
                          options={[
                            {
                              value: Country.DE,
                              label: "DE",
                              data: Country.DE,
                            },
                            {
                              value: Country.FR,
                              label: "FR",
                              data: Country.FR,
                            },
                          ]}
                          label="Country"
                          value={values.country}
                        />
                      </GridItem>

                      <GridItem width="1/3">
                        <TextInput
                          id="userId"
                          label="User ID"
                          onChange={handleChange}
                          value={values.userId}
                        />
                      </GridItem>

                      <GridItem width="1/3">
                        <TextInput
                          id="email"
                          label="Email"
                          onChange={handleChange}
                          value={values.email}
                        />
                      </GridItem>
                    </Grid>

                    <Grid>
                      <GridItem width="1/3">
                        <TextInput
                          id="firstName"
                          label="First Name"
                          onChange={handleChange}
                          value={values.firstName}
                        />
                      </GridItem>

                      <GridItem width="1/3">
                        <TextInput
                          id="lastName"
                          label="Last Name"
                          onChange={handleChange}
                          value={values.lastName}
                        />
                      </GridItem>

                      <GridItem width="1/3">
                        <TextInput
                          id="nickname"
                          label="Nickname"
                          onChange={handleChange}
                          value={values.nickname}
                        />
                      </GridItem>
                    </Grid>

                    <Grid>
                      <GridItem width="1/3">
                        <MultiSelect
                          id="role"
                          options={[
                            {
                              value: Role.SUPER_ADMIN,
                              label: "Super Admin",
                              data: Role.SUPER_ADMIN,
                            },
                            {
                              value: Role.ADMINISTRATOR,
                              label: "Admin",
                              data: Role.ADMINISTRATOR,
                            },
                            {
                              value: Role.SUPPORT,
                              label: "Support Staff",
                              data: Role.SUPPORT,
                            },
                          ]}
                          label="Role"
                          value={values.role}
                        />
                      </GridItem>
                    </Grid>
                  </div>

                  <div className={styles.btnGroup}>
                    <Grid>
                      <GridItem width="2/3">
                        <></>
                      </GridItem>
                      <GridItem width="1/3" className={styles.groupBtn}>
                        <Button
                          label="clear"
                          onClick={() => {
                            resetForm();
                          }}
                        ></Button>
                        <Button type="submit" label="search"></Button>
                      </GridItem>
                    </Grid>
                  </div>
                </div>
              </Form>
            )}
          </Formik>

          <div className={styles.tableContainer}>
            <Table
              columns={columns}
              rows={newRows}
              rowClass={(row) =>
                !row.isActiveForDEData && !row.isActiveForFRData
                  ? "highlight-row"
                  : "rdgCell"
              }
              sortColumns={sortColumns}
              onSortColumnsChange={(sortColumns) =>
                onSortColumns(sortColumns, searchCondition)
              }
            />

            {newRows.length > 0 && (
              <Pagination
                items={newRows}
                totalCount={totalUsersCount}
                forcePage={forcePage}
                getItemsPerPage={async (itemsPerPage: number) => {
                  // limit
                  let sortOptionsUpdate = { ...sortOptions };

                  sortOptionsUpdate.limit = itemsPerPage;
                  sortOptionsUpdate.page = 1;
                  setSortOptions(sortOptionsUpdate);

                  await fetchUsers(sortOptionsUpdate, searchCondition);

                  if (forcePage === 0) {
                    setForcePage(undefined);
                  }
                  if (!forcePage) {
                    setForcePage(0);
                  }
                }}
                getPageNumber={async (pageNumber: number) => {
                  // page
                  let sortOptionsUpdate = { ...sortOptions };

                  sortOptionsUpdate.page = pageNumber;
                  setSortOptions(sortOptionsUpdate);

                  await fetchUsers(sortOptionsUpdate, searchCondition);
                }}
              />
            )}

            {isShowNoRecordFoundModal && (
              <NoRecordFoundModal
                isShowing={isShowNoRecordFoundModal}
                setVisible={setIsShowNoRecordFoundModal}
              />
            )}

            <AnnouncementModal
              title="Update Permission Successfully"
              isShowing={isShowSucessfullModal}
              setVisible={setIsShowSucessfullModal}
            >
              <div className={styles.announcementText}>
                Admin user permissions have been updated successfully.
              </div>
            </AnnouncementModal>

            <ProcessFailedModal
              isShowing={isShowErrorModal}
              setVisible={() => {
                setIsShowErrorModal(false);
              }}
            />

            <Spinner
              type="fullPage"
              title={i18n._({ id: "loading" })}
              visible={isShowSpinner}
            />
          </div>

          <div className={styles.btnGroup}>
            <Grid>
              <GridItem width="2/3">
                <></>
              </GridItem>
              <GridItem width="1/3" className={styles.groupBtn}>
                <Button
                  label="Save"
                  onClick={async () => {
                    setIsShowSpinner(true);

                    try {
                      let userDataPermissions: {
                        userId: string;
                        permission: DataPermissionByCountry;
                      }[] = [];

                      newRows.forEach((row) => {
                        if (row.isActiveForDEData) {
                          userDataPermissions.push({
                            userId: row.id,
                            permission: DataPermissionByCountry.DE_DATA,
                          });
                        }

                        if (row.isActiveForFRData) {
                          userDataPermissions.push({
                            userId: row.id,
                            permission: DataPermissionByCountry.FR_DATA,
                          });
                        }
                      });

                      console.log("newRows", newRows);
                      console.log("userDataPermissions", userDataPermissions);
                      await updateDataPermissionForAdminUser(
                        userDataPermissions
                      );

                      let sortOptionsUpdate = { ...sortOptions };
                      await fetchUsers(sortOptionsUpdate, searchCondition);
                      setIsShowSucessfullModal(true);
                    } catch (err) {
                      setIsShowSpinner(false);
                      setIsShowErrorModal(true);
                    } finally {
                      setIsShowSpinner(false);
                    }
                  }}
                  disabled={isDisableSaveButton}
                ></Button>
              </GridItem>
            </Grid>
          </div>
        </div>
      </div>
    </RestrictedTo>
  );
};
